import * as Linking from "expo-linking";
import * as widgetsSV from "widgets/lang/sv.json";
import * as messagesSV from "../../lang/compiled/sv.json";

import {
  DefaultTheme,
  NavigationContainer,
  NavigationContainerRef,
  NavigationState,
  PartialState,
} from "@react-navigation/native";
import { LoginProvider, useUser } from "./Login/State";
import React, {
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AHButton,
  AHQueryHandlerLoading,
  AHText,
  Box,
  ScaleFactorProvider,
  useTheme,
} from "arkh";

import { store } from "./Store";
import { KeyboardProvider as KeyboardControllerProvider } from "react-native-keyboard-controller";
import { AHFontLoader } from "../components/mobile/AHFontLoader";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { InteractionManager } from "react-native";
import { IntlProvider } from "react-intl";
import { KeyboardProvider } from "./useKeyboard";

import { Provider } from "react-redux";

import { UpdateModal } from "./Updates/UpdateModal";
import { UserLoggedInNavigatorParamList } from "../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { registerScreen } from "../common/analytics";

import { RelayEnvironment } from "./RelayClient";

import { ErrorBoundary } from "react-error-boundary";
import { ImageUploadProvider } from "./ImageUploadProvider";

import { PortalProvider } from "@gorhom/portal";
import { getActiveSystemLocale } from "./getActiveLocale";
import { config } from "../config";
import { FullWindowOverlay } from "react-native-screens";

import { captureException } from "../common/sentry";
import { AlertWebProvider } from "../common/Alert";
import { RelayEnvironmentProvider } from "react-relay";
import { useTimeZone } from "./Timezone";
import { LightboxProvider } from "../common/lightbox/LightboxProvider";

const getActiveRoutePath = (
  state: NavigationState | PartialState<NavigationState> | undefined,
  prev = ""
): string | null => {
  if (state === undefined) {
    return null;
  }
  const route = state.routes[state.index ?? 0];

  if (route.state) {
    // Dive into nested navigators
    return getActiveRoutePath(route.state, prev + "/" + route.name);
  }
  return prev + "/" + route.name;
};

const getActiveRouteParams = (
  state: NavigationState | PartialState<NavigationState> | undefined
): object | null => {
  if (state === undefined) {
    return null;
  }
  const route = state.routes[state.index ?? 0];
  if (route.state) {
    // Dive into nested navigators
    return getActiveRouteParams(route.state);
  }
  return route.params ?? null;
};

const prefix = Linking.createURL("/");
console.log(prefix);

function getMessages() {
  const locale = getActiveSystemLocale();
  switch (locale) {
    case "en":
      return {};

    case "sv":
      return { ...messagesSV, ...widgetsSV };
    default:
      return {};
  }
}

const ENABLE_TRANSLATIONS = false;
export const AppProvider: React.FunctionComponent<any> = ({ children }) => {
  const messages = getMessages();
  const [remoteMessages, setRemoteMessages] = useState<{
    [key: string]: string;
  } | null>(null);

  const locale = useMemo(() => getActiveSystemLocale(), []);

  const [renderKey, setRenderKey] = useState(0);
  useEffect(() => {
    if (config.production || !ENABLE_TRANSLATIONS) {
      return;
    }
    let cancelled = false;
    fetch(
      "https://cdn.simplelocalize.io/9d0e2d6eca2b404fba2b928b3bc74a07/_latest/" +
        locale +
        "?v=4"
    )
      .then((r) => r.json())
      .then((r) => {
        if (!cancelled) {
          setRenderKey((k) => k + 1);
          setRemoteMessages(r);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const timezone = useTimeZone();
  return (
    <KeyboardControllerProvider>
      <LightboxProvider>
        <Suspense fallback={<AHQueryHandlerLoading />}>
          <IntlProvider
            locale={getActiveSystemLocale()}
            messages={remoteMessages ?? messages}
            defaultLocale="en"
            timeZone={timezone}
          >
            <AlertWebProvider>
              <Provider store={store}>
                <LoginProvider>
                  <KeyboardProvider>
                    <ScaleFactorProvider value={1}>
                      <AHFontLoader>
                        {ENABLE_TRANSLATIONS ? (
                          <FullWindowOverlay>
                            <AHButton
                              title="Update Locales"
                              themeType="primary"
                              onPress={() => {
                                fetch(
                                  "https://cdn.simplelocalize.io/9d0e2d6eca2b404fba2b928b3bc74a07/_latest/" +
                                    locale +
                                    "?v=" +
                                    renderKey
                                )
                                  .then((r) => r.json())
                                  .then((r) => {
                                    setRenderKey((k) => k + 1);
                                    setRemoteMessages(r);
                                  });
                              }}
                            />
                          </FullWindowOverlay>
                        ) : null}
                        <Navigation>{children}</Navigation>
                      </AHFontLoader>
                    </ScaleFactorProvider>
                  </KeyboardProvider>
                </LoginProvider>
              </Provider>
            </AlertWebProvider>
          </IntlProvider>
        </Suspense>
      </LightboxProvider>
    </KeyboardControllerProvider>
  );
};
const prefixes = [
  prefix,
  "https://storesprint.dev",
  "https://storesprint.app",
  "https://admin.storesprint.app",
  "https://admin.storesprint.dev",
];

function Navigation(props: PropsWithChildren<object>) {
  const ref =
    useRef<NavigationContainerRef<UserLoggedInNavigatorParamList>>(null);

  const theme = useTheme();

  //const { updateAvailable } = useUpdateInfo();
  //const [updatesDismissed, setUpdatesDismissed] = useState(false);

  const showUpdatesAvailable = false;

  /*useMemo(
    () => !updatesDismissed && updateAvailable,
    [updateAvailable, updatesDismissed]
  );

  */

  const navigationKey = useMemo(
    () => showUpdatesAvailable + "",
    [showUpdatesAvailable]
  );
  // we need to reset the navigation container to clean out modals

  return (
    <ImageUploadProvider>
      <ErrorBoundary
        onError={(e) => {
          captureException(e);
        }}
        fallback={
          <Box
            style={{ justifyContent: "center", alignItems: "center", flex: 1 }}
          >
            <AHText type="subtitle">Something went wrong</AHText>
            <AHButton title="Reload" themeType="primary" onPress={() => {}} />
          </Box>
        }
      >
        <Suspense fallback={<AHQueryHandlerLoading />}>
          {showUpdatesAvailable ? (
            <UpdateModal
              onDismiss={() => {
                //setUpdatesDismissed(true);
              }}
            />
          ) : null}

          <NavigationContainer
            ref={ref}
            navigationInChildEnabled
            key={navigationKey}
            documentTitle={{
              formatter: (options, route) => {
                return (
                  "StoreSprint " + (options?.title ? " – " + options.title : "")
                );
              },
            }}
            theme={{
              ...DefaultTheme,
              colors: {
                ...DefaultTheme.colors,
                primary: theme.primary,
                background: theme.background,
                card: theme.surface,
                border: theme["border-primary"],
                text: theme.darkerText,
              },
            }}
            linking={
              {
                prefixes,

                config: {
                  screens: {
                    SupportAccessEntry: {
                      path: "/support-access",
                    },
                    Login: {
                      path: "/login",
                    },
                    StorecastAdmin: {
                      screens: {
                        AddScreen: {
                          path: "/storecast/add/:userCode",
                        },
                      },
                    },
                  },
                },
              } as any
            }
            onStateChange={(state) => {
              InteractionManager.runAfterInteractions(() => {
                const name = getActiveRoutePath(state);
                if (__DEV__) {
                  console.log(`Navigated to ${name}`);
                }
                if (name) {
                  registerScreen(
                    name,
                    getActiveRouteParams(state) ?? undefined
                  );
                }
              });
            }}
          >
            <BottomSheetModalProvider>
              <ActionSheetProvider>{props.children}</ActionSheetProvider>
            </BottomSheetModalProvider>
          </NavigationContainer>
        </Suspense>
      </ErrorBoundary>
    </ImageUploadProvider>
  );
}

export const LoggedInProvider = (props: PropsWithChildren<object>) => {
  const usr = useUser();
  const user = usr.user.sub + (usr.currentStoreId ?? "");

  const relayEnv = useMemo(() => RelayEnvironment(user), [user]);

  return (
    <RelayEnvironmentProvider environment={relayEnv}>
      <PortalProvider>
        <BottomSheetModalProvider>{props.children}</BottomSheetModalProvider>
      </PortalProvider>
    </RelayEnvironmentProvider>
  );
};
